/**
 * 订单详情抽屉 - 头部区块
 *
 * 展示订单号与分享/导出/编辑/关闭操作。
 *
 * 会员墙只判在**点击那一刻**（`usePaywallGate`），按钮本身一律给正常态：
 * 曾经这里用 `permission.can()` 同时驱动置灰、锁图标和 title，而会员状态是异步拉的
 * （`/membership/status` 回来前 features 为空），付费用户刚点开抽屉就会看见一把锁、
 * 点下去什么也不发生——墙该说的没说，不该演的先演了。
 * 现在判不过弹的是「专属功能 + 是否现在去开通」，服务端 `@RequireFeature` 仍是最终裁判。
 */
import type { OrderItem } from '../../../../../types';
import { usePaywallGate } from '../../../../../utils/paywall';

interface Props {
  order: OrderItem;
  /** 后端投影 canEditOrder：建单方 + 档期在途（或审核被拒豁免单），只有它放行才给编辑入口 */
  canEdit: boolean;
  onEdit: (order: OrderItem) => void;
  onClose: () => void;
  onShowShare: () => void;
  onShowExport: () => void;
}

export default function HeaderSection({ order, canEdit, onEdit, onClose, onShowShare, onShowExport }: Props) {
  const gate = usePaywallGate();

  return (
    <>
      {/* Header */}
      <div className="bg-teal-600 px-5 py-4 flex items-center justify-between shrink-0">
        <div>
          <p className="text-xs text-teal-200 mb-0.5">订单详情</p>
          <p className="text-sm font-semibold text-white font-mono">{order.orderNumber ?? order.id.slice(0, 8).toUpperCase()}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={async () => { if (await gate('shareOrder', '分享订单')) onShowShare(); }}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white/20 hover:bg-white/30 text-white text-xs rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            title="分享订单"
          >
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-share-line text-xs" />
            </div>
            分享
          </button>
          <button
            onClick={async () => { if (await gate('exportPrint', '导出打印')) onShowExport(); }}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white/20 hover:bg-white/30 text-white text-xs rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            title="导出订单"
          >
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-download-line text-xs" />
            </div>
            导出
          </button>
          {canEdit && (
            <button onClick={() => onEdit(order)} className="flex items-center gap-1.5 px-3 py-1.5 bg-white/20 hover:bg-white/30 text-white text-xs rounded-lg transition-colors cursor-pointer whitespace-nowrap">
              <div className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-edit-line text-xs" /></div>
              编辑
            </button>
          )}
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-teal-200 hover:text-white cursor-pointer transition-colors">
            <i className="ri-close-line text-lg" />
          </button>
        </div>
      </div>
    </>
  );
}
